// Admin Panel Page
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { StatusBadge, ScoreBar } from '../components/StatusBadge';
import { statsApi, usersApi, exportApi, settingsApi, calendarApi, eventsApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Users, 
  ClipboardCheck, 
  Calendar,
  FileSpreadsheet,
  FileText,
  Download,
  Shield,
  TrendingUp,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Link2,
  CheckCircle2,
  ExternalLink,
  Archive,
  ArchiveRestore,
  UserCheck,
  AlertTriangle,
  Sparkles
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import DevelopmentProfileDialog from '../components/DevelopmentProfileDialog';

export default function AdminPage() {
  const { user, isAdmin } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all_time');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '' });
  const [peerEvalEnabled, setPeerEvalEnabled] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [calendarAuthUrl, setCalendarAuthUrl] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [authorizing, setAuthorizing] = useState(false);
  const [devProfileOpen, setDevProfileOpen] = useState(false);
  const [devProfileUser, setDevProfileUser] = useState(null);

  const openDevProfile = (u) => {
    setDevProfileUser(u);
    setDevProfileOpen(true);
  };

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    loadData();
    loadPeerEvalSetting();
    loadCalendarStatus();
  }, [isAdmin, period, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [overviewRes, usersRes] = await Promise.all([
        statsApi.getOverview(period),
        usersApi.getAll()
      ]);
      setOverview(overviewRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPeerEvalSetting = async () => {
    try {
      const res = await settingsApi.getPeerEvaluation();
      setPeerEvalEnabled(res.data.enabled);
    } catch (error) {
      console.error('Error loading peer eval setting:', error);
    }
  };

  const handleTogglePeerEval = async (checked) => {
    try {
      await settingsApi.setPeerEvaluation(checked);
      setPeerEvalEnabled(checked);
      toast.success(
        checked
          ? (language === 'el' ? 'Ομότιμη αξιολόγηση ενεργοποιήθηκε!' : 'Peer evaluation enabled!')
          : (language === 'el' ? 'Ομότιμη αξιολόγηση απενεργοποιήθηκε!' : 'Peer evaluation disabled!')
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const loadCalendarStatus = async () => {
    try {
      const res = await calendarApi.getStatus();
      setCalendarStatus(res.data);
    } catch (error) {
      console.error('Error loading calendar status:', error);
    }
  };

  const handleClearInvitations = async () => {
    if (!window.confirm(language === 'el' 
      ? 'Διαγραφή όλων των δεδομένων διαθεσιμότητας (Αποδοχές/Απορρίψεις/Εκκρεμούν);' 
      : 'Clear all availability data (Accepted/Declined/Pending)?')) return;
    try {
      await eventsApi.clearAllInvitations();
      toast.success(language === 'el' ? 'Τα δεδομένα διαθεσιμότητας διαγράφηκαν!' : 'Availability data cleared!');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleGetAuthUrl = async () => {
    try {
      const res = await calendarApi.getAuthUrl();
      setCalendarAuthUrl(res.data.auth_url);
      window.open(res.data.auth_url, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleAuthorize = async () => {
    if (!authCode.trim()) return;
    setAuthorizing(true);
    try {
      // Extract just the code if user pasted the full redirect URL
      let code = authCode.trim();
      if (code.includes('code=')) {
        try {
          const url = new URL(code);
          code = url.searchParams.get('code') || code;
        } catch {
          const match = code.match(/[?&]code=([^&]+)/);
          if (match) code = match[1];
        }
      }
      await calendarApi.authorize(code);
      toast.success(language === 'el' ? 'Google Calendar συνδέθηκε!' : 'Google Calendar connected!');
      setAuthCode('');
      setCalendarAuthUrl('');
      loadCalendarStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authorization failed');
    } finally {
      setAuthorizing(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await usersApi.updateRole(userId, newRole);
      toast.success(language === 'el' ? 'Ρόλος ενημερώθηκε!' : 'Role updated!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleEditUser = (u) => {
    setEditingUser(u);
    setEditForm({ name: u.name, email: u.email });
    setEditDialogOpen(true);
  };

  const handleSaveUser = async () => {
    try {
      await usersApi.adminUpdate(editingUser.user_id, editForm);
      toast.success(language === 'el' ? 'Χρήστης ενημερώθηκε!' : 'User updated!');
      setEditDialogOpen(false);
      setEditingUser(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(
      language === 'el' 
        ? `Είστε σίγουροι ότι θέλετε να διαγράψετε τον χρήστη "${userName}";`
        : `Are you sure you want to delete user "${userName}"?`
    )) return;
    
    try {
      await usersApi.delete(userId);
      toast.success(language === 'el' ? 'Χρήστης διαγράφηκε!' : 'User deleted!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleToggleProbation = async (userId, currentValue) => {
    try {
      await usersApi.adminUpdate(userId, { is_probation: !currentValue });
      toast.success(!currentValue
        ? (language === 'el' ? 'Ορίστηκε ως Δόκιμος' : 'Set as Probation')
        : (language === 'el' ? 'Αφαιρέθηκε από Δόκιμος' : 'Removed from Probation'));
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleToggleArchive = async (userId, userName, currentValue) => {
    const action = !currentValue
      ? (language === 'el' ? `Αρχειοθέτηση "${userName}"; Δεν θα λαμβάνει προσκλήσεις.` : `Archive "${userName}"? They won't receive invitations.`)
      : (language === 'el' ? `Επαναφορά "${userName}"; Θα λαμβάνει ξανά προσκλήσεις.` : `Restore "${userName}"? They will receive invitations again.`);
    if (!window.confirm(action)) return;
    try {
      await usersApi.adminUpdate(userId, { is_archived: !currentValue });
      toast.success(!currentValue
        ? (language === 'el' ? 'Αρχειοθετήθηκε' : 'Archived')
        : (language === 'el' ? 'Επαναφέρθηκε' : 'Restored'));
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleExportExcel = () => {
    exportApi.toExcel({ period });
    toast.success(language === 'el' ? 'Εξαγωγή ξεκίνησε...' : 'Export started...');
  };

  const handleExportPdf = () => {
    exportApi.toPdf({ period });
    toast.success(language === 'el' ? 'Εξαγωγή ξεκίνησε...' : 'Export started...');
  };

  const timePeriods = [
    { value: 'this_month', label: t('thisMonth') },
    { value: 'quarter', label: t('quarter') },
    { value: 'semester', label: t('semester') },
    { value: 'year', label: t('year') },
    { value: 'two_years', label: t('twoYears') },
    { value: 'all_time', label: t('allTime') },
  ];

  const statusColors = {
    green: '#22c55e',
    yellow: '#eab308',
    red: '#ef4444'
  };

  const pieData = overview ? [
    { name: language === 'el' ? 'Άριστο' : 'Excellent', value: overview.status_distribution.green, color: statusColors.green },
    { name: language === 'el' ? 'Επαρκές' : 'Acceptable', value: overview.status_distribution.yellow, color: statusColors.yellow },
    { name: language === 'el' ? 'Χρειάζεται Βελτίωση' : 'Needs Improvement', value: overview.status_distribution.red, color: statusColors.red },
  ].filter(d => d.value > 0) : [];

  const getRoleBadge = (role) => {
    const config = {
      admin: { label: t('admin'), variant: 'default', className: 'bg-purple-500' },
      senior_facilitator: { label: t('seniorFacilitator'), variant: 'default', className: 'bg-blue-500' },
      facilitator: { label: t('facilitator'), variant: 'secondary', className: '' }
    };
    const c = config[role] || config.facilitator;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in-up" data-testid="admin-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-[#2A1514]">{t('adminPanel')}</h1>
            <p className="text-gray-500 mt-1">
              {language === 'el' 
                ? 'Διαχείριση χρηστών και αναφορές'
                : 'User management and reports'
              }
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[180px]" data-testid="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timePeriods.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleExportExcel} data-testid="export-excel-btn">
              <FileSpreadsheet size={18} className="mr-2" />
              Excel
            </Button>
            <Button variant="outline" onClick={handleExportPdf} data-testid="export-pdf-btn">
              <FileText size={18} className="mr-2" />
              PDF
            </Button>
          </div>
        </div>

        {/* Peer Evaluation & Google Calendar — compact row */}
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[220px] flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-[#E6007E]/5 to-pink-50 border border-pink-100">
            <Shield className="text-[#E6007E] shrink-0" size={16} />
            <span className="text-xs font-medium text-[#2A1514] truncate">
              {language === 'el' ? 'Ομότιμη Αξιολόγηση' : 'Peer Evaluation'}
            </span>
            <span className={`text-[10px] ml-auto mr-1 ${peerEvalEnabled ? 'text-green-600' : 'text-gray-400'}`}>
              {peerEvalEnabled ? (language === 'el' ? 'Ενεργό' : 'ON') : (language === 'el' ? 'Ανενεργό' : 'OFF')}
            </span>
            <Switch checked={peerEvalEnabled} onCheckedChange={handleTogglePeerEval} data-testid="peer-eval-toggle" className="scale-90" />
          </div>

          <div className="flex-1 min-w-[220px] flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-100">
            <Calendar className="text-blue-600 shrink-0" size={16} />
            <span className="text-xs font-medium text-[#2A1514] truncate">Google Calendar</span>
            {calendarStatus?.authorized ? (
              <>
                <span className="text-[10px] text-gray-500 truncate ml-auto">{calendarStatus.email}</span>
                <CheckCircle2 size={14} className="text-green-500 shrink-0" />
              </>
            ) : (
              <Button variant="outline" size="sm" onClick={handleGetAuthUrl} className="ml-auto h-6 px-2 text-[10px] border-blue-300 text-blue-600 hover:bg-blue-50" data-testid="connect-calendar-btn">
                <Link2 size={12} className="mr-1" />
                {language === 'el' ? 'Σύνδεση' : 'Connect'}
              </Button>
            )}
          </div>

          <Button variant="outline" size="sm" onClick={handleClearInvitations} className="h-auto py-2 px-3 text-[10px] border-red-200 text-red-500 hover:bg-red-50" data-testid="clear-invitations-btn">
            <Trash2 size={12} className="mr-1" />
            {language === 'el' ? 'Εκκαθάριση Διαθεσιμότητας' : 'Clear Availability'}
          </Button>
        </div>

        {/* Calendar Auth Code Input (only when connecting) */}
        {calendarAuthUrl && !calendarStatus?.authorized && (
          <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-200 space-y-2">
            <p className="text-xs text-gray-600">
              {language === 'el'
                ? '1. Συνδέσου με info.summit.gr@gmail.com και δώσε πρόσβαση. 2. Αντίγραψε ολόκληρο το URL και επικόλλησέ το εδώ:'
                : '1. Sign in and grant access. 2. Copy the entire URL from the redirect page and paste it here:'}
            </p>
            <div className="flex gap-2">
              <Input
                placeholder={language === 'el' ? 'Επικόλλησε το URL ή τον κωδικό εδώ...' : 'Paste URL or code here...'}
                value={authCode}
                onChange={(e) => setAuthCode(e.target.value)}
                className="text-sm h-8"
                data-testid="auth-code-input"
              />
              <Button
                onClick={handleAuthorize}
                disabled={authorizing || !authCode.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-xs h-8"
                data-testid="submit-auth-code-btn"
              >
                {authorizing ? '...' : (language === 'el' ? 'Σύνδεση' : 'Connect')}
              </Button>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#E6007E]/10 rounded-xl flex items-center justify-center">
                  <Users className="text-[#E6007E]" size={24} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{language === 'el' ? 'Συνολικοί Χρήστες' : 'Total Users'}</p>
                  <p className="text-2xl font-bold text-[#2A1514]">{overview?.total_users || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <ClipboardCheck className="text-blue-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('totalEvaluations')}</p>
                  <p className="text-2xl font-bold text-[#2A1514]">{overview?.total_evaluations || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Calendar className="text-green-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('totalEvents')}</p>
                  <p className="text-2xl font-bold text-[#2A1514]">{overview?.total_events || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{language === 'el' ? 'Κατανομή Status' : 'Status Distribution'}</p>
                  <div className="flex gap-2 mt-2">
                    <span className="text-green-600 font-bold">{overview?.status_distribution?.green || 0}</span>
                    <span className="text-yellow-600 font-bold">{overview?.status_distribution?.yellow || 0}</span>
                    <span className="text-red-600 font-bold">{overview?.status_distribution?.red || 0}</span>
                  </div>
                </div>
                <TrendingUp className="text-gray-400" size={24} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="users">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="users">{language === 'el' ? 'Χρήστες' : 'Users'}</TabsTrigger>
            <TabsTrigger value="stats">{language === 'el' ? 'Στατιστικά' : 'Statistics'}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="users" className="mt-6">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="font-heading">{language === 'el' ? 'Διαχείριση Χρηστών' : 'User Management'}</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[1,2,3,4,5].map(i => (
                      <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{language === 'el' ? 'Χρήστης' : 'User'}</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>{language === 'el' ? 'Ρόλος' : 'Role'}</TableHead>
                        <TableHead>{language === 'el' ? 'Κατάσταση' : 'Tags'}</TableHead>
                        <TableHead>{language === 'el' ? 'Βαθμολογία' : 'Score'}</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>{language === 'el' ? 'Ενέργειες' : 'Actions'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map(u => {
                        const userStats = overview?.user_stats?.[u.user_id];
                        return (
                          <TableRow key={u.user_id} className={u.is_archived ? 'opacity-50' : ''}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <Avatar className="h-8 w-8">
                                  <AvatarImage src={u.picture} />
                                  <AvatarFallback className="bg-[#E6007E] text-white text-xs">
                                    {u.name?.charAt(0)?.toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="font-medium">{u.name}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-gray-500">{u.email}</TableCell>
                            <TableCell>{getRoleBadge(u.role)}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {u.is_probation && (
                                  <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-600 bg-amber-50">
                                    {language === 'el' ? 'Δόκιμος' : 'Probation'}
                                  </Badge>
                                )}
                                {u.is_archived && (
                                  <Badge variant="outline" className="text-[10px] border-gray-400 text-gray-500 bg-gray-50">
                                    {language === 'el' ? 'Αρχείο' : 'Archived'}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {userStats ? (
                                <span className="font-medium">{userStats.average_score.toFixed(1)}</span>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {userStats ? (
                                <StatusBadge status={userStats.status} size="small" showLabel={false} />
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {u.user_id !== user?.user_id && u.role !== 'admin' && (
                                  <>
                                    <Select 
                                      value={u.role} 
                                      onValueChange={(v) => handleRoleChange(u.user_id, v)}
                                    >
                                      <SelectTrigger className="w-[130px] h-8" data-testid={`role-select-${u.user_id}`}>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="facilitator">{t('facilitator')}</SelectItem>
                                        <SelectItem value="senior_facilitator">{t('seniorFacilitator')}</SelectItem>
                                        <SelectItem value="admin">{t('admin')}</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className={`h-7 w-7 ${u.is_probation ? 'text-amber-500' : 'text-gray-400 hover:text-amber-500'}`}
                                      onClick={() => handleToggleProbation(u.user_id, u.is_probation)}
                                      title={language === 'el' ? 'Δόκιμος' : 'Probation'}
                                      data-testid={`probation-toggle-${u.user_id}`}
                                    >
                                      <AlertTriangle size={14} />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className={`h-7 w-7 ${u.is_archived ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50' : 'text-gray-400 hover:text-gray-600'}`}
                                      onClick={() => handleToggleArchive(u.user_id, u.name, u.is_archived)}
                                      title={u.is_archived
                                        ? (language === 'el' ? 'Επαναφορά από αρχείο' : 'Restore from archive')
                                        : (language === 'el' ? 'Αρχειοθέτηση' : 'Archive')}
                                      data-testid={`archive-toggle-${u.user_id}`}
                                    >
                                      {u.is_archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-gray-400 hover:text-purple-600"
                                      onClick={() => openDevProfile(u)}
                                      title={language === 'el' ? 'Προφίλ Ανάπτυξης' : 'Development Profile'}
                                      data-testid={`dev-profile-${u.user_id}`}
                                    >
                                      <Sparkles size={14} />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-gray-400 hover:text-blue-600"
                                      onClick={() => handleEditUser(u)}
                                      data-testid={`edit-user-${u.user_id}`}
                                    >
                                      <Edit size={14} />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-gray-400 hover:text-red-600"
                                      onClick={() => handleDeleteUser(u.user_id, u.name)}
                                      data-testid={`delete-user-${u.user_id}`}
                                    >
                                      <Trash2 size={14} />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="stats" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Status Distribution Chart */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="font-heading">
                    {language === 'el' ? 'Κατανομή Status' : 'Status Distribution'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">
                      {t('noData')}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top Performers */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="font-heading">{t('topPerformers')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {overview?.user_stats && Object.keys(overview.user_stats).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(overview.user_stats)
                        .sort((a, b) => b[1].average_score - a[1].average_score)
                        .slice(0, 5)
                        .map(([userId, stats], idx) => (
                          <div key={userId} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                            <span className={`
                              w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                              ${idx === 0 ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-200 text-gray-600'}
                            `}>
                              {idx + 1}
                            </span>
                            <div className="flex-1">
                              <p className="font-medium text-[#2A1514]">{stats.name}</p>
                              <p className="text-xs text-gray-500">
                                {stats.evaluation_count} {language === 'el' ? 'αξιολογήσεις' : 'evaluations'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className={`font-bold ${
                                stats.average_score >= 4 ? 'text-green-600' :
                                stats.average_score >= 3 ? 'text-yellow-600' :
                                'text-red-600'
                              }`}>
                                {stats.average_score.toFixed(1)}
                              </p>
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">
                      {t('noData')}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {language === 'el' ? 'Επεξεργασία Χρήστη' : 'Edit User'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('name')}</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                data-testid="edit-user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('email')}</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                data-testid="edit-user-email-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} data-testid="edit-user-cancel-btn">
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleSaveUser} 
              className="bg-[#E6007E] hover:bg-[#b8005f]"
              data-testid="edit-user-save-btn"
            >
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DevelopmentProfileDialog
        open={devProfileOpen}
        onOpenChange={setDevProfileOpen}
        userId={devProfileUser?.user_id}
        userName={devProfileUser?.name}
        language={language}
      />
    </Layout>
  );
}
